import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import adminConsoleService, {
  type AdminUser,
  type SecurityGroupListItem,
} from '@services/adminConsoleService';
import { InviteNewUsers } from '../InviteNewUsers';
import EditSecurityGroupsModal from '@/components/ConversationItem/modals/EditSecurityGroupPermissions/EditSecurityGroupPermissionsModal';

export const UsersTab: React.FC = () =>
{
  const [showInvite, setShowInvite] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [securityGroupList, setSecurityGroupList] = useState<SecurityGroupListItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline name editing
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const committedRef = useRef(false);

  // Edit Security Groups modal
  const [editSecGroupsUser, setEditSecGroupsUser] = useState<{
    personId: number;
    name: string;
  } | null>(null);

  // Confirm delete
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Resend invitation toast
  const [resentEmail, setResentEmail] = useState<string | null>(null);

  // Statistics panel
  const [showStats, setShowStats] = useState(false);

  // Virtual scroll
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(() =>
  {
    setLoading(true);
    Promise.all([adminConsoleService.getAdminUsers(), adminConsoleService.getSecurityGroupList()])
      .then(([usersData, groupsData]) =>
      {
        setUsers(usersData);
        setSecurityGroupList(groupsData);
      })
      .catch(() => setError('Failed to load users. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() =>
  {
    loadData();
  }, [loadData]);

  const startEditName = (user: AdminUser) =>
  {
    committedRef.current = false;
    setEditingId(user.PersonID);
    setEditingValue(user.Name);
  };

  const commitEditName = async () =>
  {
    if (committedRef.current || editingId === null) return;
    committedRef.current = true;
    const trimmed = editingValue.trim();
    const original = users.find(u => u.PersonID === editingId);
    setEditingId(null);
    if (!trimmed || trimmed === original?.Name) return;
    // Optimistic update
    setUsers(prev => prev.map(u => (u.PersonID === editingId ? { ...u, Name: trimmed } : u)));
    try
    {
      await adminConsoleService.updatePersonName(editingId, trimmed);
    }
    catch
    {
      setError('Failed to update name.');
      setUsers(prev =>
        prev.map(u => (u.PersonID === editingId ? { ...u, Name: original!.Name } : u)),
      );
    }
  };

  const cancelEditName = () =>
  {
    committedRef.current = true;
    setEditingId(null);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      commitEditName();
    }
    if (e.key === 'Escape')
    {
      cancelEditName();
    }
    if (e.key === 'Tab')
    {
      commitEditName();
    }
  };

  // ── Edit Security Groups ──────────────────────────────────────────────────
  const openEditSecGroups = (user: AdminUser) =>
  {
    setEditSecGroupsUser({ personId: user.PersonID, name: user.Name });
  };

  // ── Toggle boolean flags ──────────────────────────────────────────────────
  const toggleFlag = async (
    user: AdminUser,
    field: 'PTOApprover' | 'HRApprover' | 'SubmitTimeTeam',
    value: boolean,
  ) =>
  {
    setUsers(prev => prev.map(u => (u.PersonID === user.PersonID ? { ...u, [field]: value } : u)));
    try
    {
      if (field === 'PTOApprover')
        await adminConsoleService.updatePTOApprover(user.PersonID, value);
      else if (field === 'HRApprover')
        await adminConsoleService.updateHRApprover(user.PersonID, value);
      else await adminConsoleService.updateSubmitTimeTeam(user.PersonID, value);
    }
    catch
    {
      setError(`Failed to update ${field}.`);
      setUsers(prev =>
        prev.map(u => (u.PersonID === user.PersonID ? { ...u, [field]: !value } : u)),
      );
    }
  };

  // ── Toggle Person Status (Disable / Restore) ──────────────────────────────
  const executeDelete = async () =>
  {
    if (!confirmDeleteUser) return;
    const restore = !confirmDeleteUser.IsEnabled;
    setDeleting(true);
    try
    {
      await adminConsoleService.toggleUserStatus(confirmDeleteUser.PersonID, restore);
      setUsers(prev => prev.filter(u => u.PersonID !== confirmDeleteUser.PersonID));
      setConfirmDeleteUser(null);
    }
    catch
    {
      setError(`Failed to ${restore ? 'restore' : 'disable'} user.`);
      setConfirmDeleteUser(null);
    }
    finally
    {
      setDeleting(false);
    }
  };

  // ── Resend Invitation ─────────────────────────────────────────────────────
  const resendInvitation = async (user: AdminUser) =>
  {
    try
    {
      await adminConsoleService.resendInvitation(user.PersonID);
      setResentEmail(user.EmailAddress ?? user.Name);
      setTimeout(() => setResentEmail(null), 3000);
    }
    catch
    {
      setError('Failed to resend invitation.');
    }
  };

  // Map SecurityGroupID → GroupName using the group list
  const getDefaultGroupName = (securityGroupID: number): string =>
  {
    const found = securityGroupList.find(g => g.GroupId === securityGroupID);
    return found?.GroupName ?? '—';
  };

  const filtered = search.trim()
    ? users.filter(
      u =>
        u.Name.toLowerCase().includes(search.toLowerCase()) ||
          u.EmailAddress?.toLowerCase().includes(search.toLowerCase()),
    )
    : users;

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 48,
    overscan: 8,
    measureElement: el => el.getBoundingClientRect().height,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
      : 0;

  const CheckCell: React.FC<{ id: string; checked: boolean; onChange: () => void }> = ({
    id,
    checked,
    onChange,
  }) => (
    <div className="flex justify-center">
      <span className="corgee_com_checkbox">
        <input id={id} type="checkbox" checked={checked} onChange={onChange} />
        <label htmlFor={id}></label>
      </span>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-[#555]">Find by keyword:</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search users..."
              className="border border-[#ccc] rounded px-2 py-1 text-sm text-[#333] outline-none focus:border-[#62aacb] w-[220px]"
            />
          </div>
          <button
            onClick={() => setShowStats(prev => !prev)}
            className="flex items-center gap-1 text-[#62aacb] text-[12px] "
          >
            <span className="hover:underline">Users Statistics</span>
            <span className="material-icons !text-[17px] text-[#00000066] dark:text-[#beb7ac]">
              {showStats ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        </div>
        <button
          className="flex items-center gap-1 text-[#62aacb] text-[12px]"
          onClick={() => setShowInvite(true)}
        >
          <span className="material-icons !text-[17px] text-[#00000066] dark:text-[#beb7ac]">
            add_circle_outline
          </span>
          <span className="hover:underline">Add People</span>
        </button>
      </div>

      {/* Statistics Panel */}
      {showStats && !loading && (
        <div className="mx-6 mb-3 px-4 py-3 bg-[#f5fafd] border border-[#c8e0ef] rounded">
          <div className="text-[11px] font-semibold text-[#62aacb] mb-2 uppercase tracking-wide">
            Users Statistics
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2">
            {[
              { label: 'Total Users', value: users.length },
              { label: 'Enabled', value: users.filter(u => u.IsEnabled).length },
              { label: 'Disabled', value: users.filter(u => !u.IsEnabled).length },
              { label: 'Accepted', value: users.filter(u => u.Status).length },
              { label: 'Pending', value: users.filter(u => !u.Status).length },
              { label: 'External', value: users.filter(u => u.External).length },
              { label: 'PTO Approvers', value: users.filter(u => u.PTOApprover).length },
              { label: 'HR Approvers', value: users.filter(u => u.HRApprover).length },
              { label: 'Time Team', value: users.filter(u => u.SubmitTimeTeam).length },
              { label: 'Admin Projects', value: users.filter(u => u.IsAdminProject).length },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-[#666]">{label}:</span>
                <span className="text-[12px] font-semibold text-[#333]">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-6 mb-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-[#c62828] hover:opacity-70">
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* Table */}
      <div ref={scrollRef} className="flex-1 overflow-auto border-t border-[#e8e8e8]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px] w-[24px]">person_off</span>
            {search ? 'No users match your search.' : 'No users found.'}
          </div>
        ) : (
          <table className="w-full text-[12px] border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                <th className="text-left px-4 py-2 font-semibold text-[#555] w-[450px]">Users</th>
                <th className="text-left px-4 py-2 font-semibold text-[#555] w-[150px]">
                  Default Security Group
                </th>
                <th className="text-left px-4 py-2 font-semibold text-[#555] w-[350px]">
                  Security Groups
                </th>
                <th className="text-left px-4 py-2 font-semibold text-[#555] w-[180px]">Email</th>
                <th className="text-left px-4 py-2 font-semibold text-[#555] w-[80px]">Status</th>
                <th className="text-center px-2 py-2 font-semibold text-[#555] w-[65px] leading-tight">
                  PTO Approver
                </th>
                <th className="text-center px-2 py-2 font-semibold text-[#555] w-[65px] leading-tight">
                  HR Approver
                </th>
                <th className="text-center px-2 py-2 font-semibold text-[#555] w-[65px] leading-tight">
                  Time Team
                </th>
                <th className="text-center px-2 py-2 font-semibold text-[#555] w-[130px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Top spacer */}
              {paddingTop > 0 && (
                <tr>
                  <td colSpan={9} style={{ height: paddingTop, padding: 0 }} />
                </tr>
              )}

              {virtualItems.map(vRow =>
              {
                const user = filtered[vRow.index];
                if (!user) return null;
                return (
                  <tr
                    key={user.PersonID}
                    data-index={vRow.index}
                    ref={virtualizer.measureElement}
                    className="border-b border-[#f0f0f0] hover:bg-[#fafafa] align-top"
                  >
                    {/* Name */}
                    <td className="px-4 py-2">
                      {editingId === user.PersonID ? (
                        <div className="flex flex-row gap-2">
                          <input
                            autoFocus
                            type="text"
                            value={editingValue}
                            onChange={e => setEditingValue(e.target.value)}
                            onKeyDown={handleNameKeyDown}
                            className="border border-[#62aacb] rounded px-1 py-0.5 text-[12px] text-[#333] outline-none w-full max-w-[430px]"
                          />
                          <button
                            onClick={() => commitEditName()}
                            className="px-4 py-1 text-[11px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] text-center"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => cancelEditName()}
                            className="px-4 py-1 text-[11px] bg-[#a5a7ac] text-white rounded hover:bg-[#929498] text-center"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="group flex items-center gap-1 text-[#62aacb] text-left font-medium"
                          onDoubleClick={() => startEditName(user)}
                          title="Double click to rename"
                        >
                          {user.Name}
                          <span className="material-icons text-[11px] text-[#bbb] opacity-0 group-hover:opacity-100 transition-opacity">
                            edit
                          </span>
                        </button>
                      )}
                    </td>

                    {/* Default Security Group */}
                    <td className="px-4 py-2 text-[#555]">
                      {getDefaultGroupName(user.SecurityGroupID)}
                    </td>

                    {/* Security Groups */}
                    <td className="px-4 py-2">
                      {user.SecurityGroups?.length > 0 ? (
                        <ul className="list-none m-0 p-0">
                          {user.SecurityGroups.map(sg => (
                            <li key={sg.SecurityGroupID} className="flex items-start gap-1">
                              <span className="text-[#aaa] flex-shrink-0">›</span>
                              <span className="text-[#555]">{sg.Name}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-[#ccc]">—</span>
                      )}
                    </td>

                    {/* Email */}
                    <td className="px-4 py-2 text-[#555] break-all">
                      {user.EmailAddress || <span className="text-[#ccc]">—</span>}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-2 text-[#555]">
                      {user.Status === true ? 'Accepted' : user.Status === false ? 'Sent' : ''}
                    </td>

                    {/* PTO Approver */}
                    <td className="px-2 py-2">
                      <CheckCell
                        id={`users-cb-pto-${user.PersonID}`}
                        checked={!!user.PTOApprover}
                        onChange={() => toggleFlag(user, 'PTOApprover', !user.PTOApprover)}
                      />
                    </td>

                    {/* HR Approver */}
                    <td className="px-2 py-2">
                      <CheckCell
                        id={`users-cb-hr-${user.PersonID}`}
                        checked={!!user.HRApprover}
                        onChange={() => toggleFlag(user, 'HRApprover', !user.HRApprover)}
                      />
                    </td>

                    {/* Time Team */}
                    <td className="px-2 py-2">
                      <CheckCell
                        id={`users-cb-time-${user.PersonID}`}
                        checked={!!user.SubmitTimeTeam}
                        onChange={() => toggleFlag(user, 'SubmitTimeTeam', !user.SubmitTimeTeam)}
                      />
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1 items-stretch">
                        <button
                          onClick={() => openEditSecGroups(user)}
                          className="px-2 py-1 text-[11px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-center"
                        >
                          Edit Security Groups
                        </button>
                        {user.Status === false && (
                          <button
                            onClick={() => resendInvitation(user)}
                            className="px-2 py-1 text-[11px] bg-[#f5a623] text-white rounded hover:bg-[#d48a0e] dark:bg-[#7a5210] dark:hover:brightness-[0.85] text-center"
                          >
                            Resend Invitation
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDeleteUser(user)}
                          className={
                            user.IsEnabled
                              ? 'px-2 py-1 text-[11px] bg-[#e57373] text-white rounded hover:bg-[#c62828] dark:bg-[#8d312c] dark:hover:brightness-[0.85] text-center'
                              : 'px-2 py-1 text-[11px] bg-[#5cb85c] text-white rounded hover:bg-[#449d44] dark:bg-[#2f6b2f] dark:hover:brightness-[0.85] text-center'
                          }
                        >
                          {user.IsEnabled ? 'Delete' : 'Restore'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {/* Bottom spacer */}
              {paddingBottom > 0 && (
                <tr>
                  <td colSpan={9} style={{ height: paddingBottom, padding: 0 }} />
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Floating Add button */}
      <button
        className="absolute bottom-12 right-12 w-[56px] h-[56px] rounded-full bg-[#62aacb] text-white shadow-lg hover:bg-[#4e99ba] flex items-center justify-center z-50"
        title="Add People"
        onClick={() => setShowInvite(true)}
      >
        <span className="icon-plus-icon text-[20x]"></span>
      </button>

      {/* Invite new users modal */}
      {showInvite && (
        <InviteNewUsers
          onCancel={() => setShowInvite(false)}
          onSuccess={() =>
          {
            loadData();
          }}
        />
      )}

      {/* ── Edit Security Groups Modal ── */}
      <EditSecurityGroupsModal
        open={!!editSecGroupsUser}
        onClose={() => setEditSecGroupsUser(null)}
        personId={editSecGroupsUser?.personId ?? 0}
        personName={editSecGroupsUser?.name ?? ''}
        onSaved={loadData}
      />

      {/* ── Resend Invitation Toast ── */}
      {resentEmail &&
        createPortal(
          <div className="admin-console-portal fixed bottom-8 left-1/2 -translate-x-1/2 z-[10004] px-5 py-3 bg-[#333] text-white text-[13px] rounded shadow-lg">
            Email invitation has been resent to {resentEmail}
          </div>,
          document.body,
        )}

      {/* ── Confirm Delete Modal ── */}
      {confirmDeleteUser &&
        createPortal(
          <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/30" onClick={() => setConfirmDeleteUser(null)} />
            <div className="relative z-10 bg-white rounded shadow-xl p-6 w-[360px]">
              <h3 className="text-[15px] font-semibold text-[#333] mb-2">
                {confirmDeleteUser.IsEnabled ? 'Delete User' : 'Restore User'}
              </h3>
              <p className="text-[13px] text-[#555] mb-5">
                {confirmDeleteUser.IsEnabled ? (
                  <>
                    Are you sure you want to delete{' '}
                    <span className="font-semibold">"{confirmDeleteUser.Name}"</span>? This action
                    cannot be undone.
                  </>
                ) : (
                  <>
                    Are you sure you want to restore{' '}
                    <span className="font-semibold">"{confirmDeleteUser.Name}"</span>?
                  </>
                )}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDeleteUser(null)}
                  className="px-3 py-1.5 text-[12px] border border-[#ccc] text-[#555] rounded hover:bg-[#f5f5f5]"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDelete}
                  disabled={deleting}
                  className={
                    confirmDeleteUser.IsEnabled
                      ? 'px-3 py-1.5 text-[12px] bg-[#e57373] text-white rounded hover:bg-[#c62828] disabled:opacity-50'
                      : 'px-3 py-1.5 text-[12px] bg-[#5cb85c] text-white rounded hover:bg-[#449d44] disabled:opacity-50'
                  }
                >
                  {deleting
                    ? confirmDeleteUser.IsEnabled
                      ? 'Deleting...'
                      : 'Restoring...'
                    : confirmDeleteUser.IsEnabled
                      ? 'Delete'
                      : 'Restore'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
